"use client";

import { useState } from "react";
import { Plus } from "@/components/icons";
import { Button } from "@/components/ui/button";
import type { ParsedAddress } from "@/components/inputs/address-types";
import { cn } from "@/lib/utils";
import { ADDRESS_CONSTRAINTS } from "@shared/formats/address";
import {
  ManualAddressActions,
  ManualTextField,
  RegionPostalRow,
} from "./manual-address-fields";
import {
  displayFor,
  toParsedAddress,
  useManualAddressDraft,
} from "./use-manual-address-draft";

type Density = "flow" | "row";

interface ManualAddressEntryProps {
  disabled: boolean;
  onSubmit: (display: string, address: ParsedAddress) => void;
  density?: Density;
  submitLabel?: string;
  idPrefix?: string;
  includeCanada?: boolean;
}

export function ManualAddressEntry({
  disabled,
  onSubmit,
  density = "flow",
  submitLabel = "Add pickup location",
  idPrefix = "manual-address",
  includeCanada = false,
}: ManualAddressEntryProps) {
  const [isOpen, setIsOpen] = useState(false);
  if (!isOpen) {
    return (
      <ManualEntryTrigger
        density={density}
        disabled={disabled}
        onOpen={() => setIsOpen(true)}
      />
    );
  }
  return (
    <ManualAddressForm
      density={density}
      disabled={disabled}
      idPrefix={idPrefix}
      includeCanada={includeCanada}
      submitLabel={submitLabel}
      onCancel={() => setIsOpen(false)}
      onSubmit={(display, address) => {
        onSubmit(display, address);
        setIsOpen(false);
      }}
    />
  );
}

type TextFieldKey = "line1" | "line2" | "city";

const TEXT_FIELDS: ReadonlyArray<{
  key: TextFieldKey;
  label: string;
  autoComplete: string;
  maxLength: number;
  optional?: boolean;
  placeholder?: string;
}> = [
  {
    key: "line1",
    label: "Street address",
    autoComplete: "address-line1",
    maxLength: ADDRESS_CONSTRAINTS.line1.maxLength,
  },
  {
    key: "line2",
    label: "Address line 2",
    autoComplete: "address-line2",
    maxLength: ADDRESS_CONSTRAINTS.line2.maxLength,
    optional: true,
    placeholder: "Apt, suite, unit, or dock",
  },
  {
    key: "city",
    label: "City",
    autoComplete: "address-level2",
    maxLength: ADDRESS_CONSTRAINTS.city.maxLength,
  },
];

interface ManualAddressFormProps {
  density: Density;
  disabled: boolean;
  idPrefix: string;
  includeCanada: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (display: string, address: ParsedAddress) => void;
}

function ManualAddressForm({
  density,
  disabled,
  idPrefix,
  includeCanada,
  submitLabel,
  onCancel,
  onSubmit,
}: ManualAddressFormProps) {
  const { draft, update, onZipGeocode, zipError, canSubmit } = useManualAddressDraft();
  const compact = density === "row";
  const submit = () => {
    const parsed = toParsedAddress(draft);
    onSubmit(displayFor(parsed), parsed);
  };
  return (
    <div
      role="group"
      aria-label="Enter address manually"
      className={cn("grid gap-3", compact && "gap-2 pt-1")}
    >
      {TEXT_FIELDS.map(({ key: fieldKey, ...field }) => (
        <ManualTextField
          key={fieldKey}
          id={`${idPrefix}-${fieldKey}`}
          {...field}
          value={draft[fieldKey]}
          onChange={(value) => update({ [fieldKey]: value })}
          compact={compact}
        />
      ))}
      <RegionPostalRow
        idPrefix={idPrefix}
        draft={draft}
        update={update}
        onZipGeocode={onZipGeocode}
        zipError={zipError}
        includeCanada={includeCanada}
        compact={compact}
      />
      <ManualAddressActions
        submitLabel={submitLabel}
        canSubmit={canSubmit && !disabled}
        onCancel={onCancel}
        onSubmit={submit}
      />
    </div>
  );
}

function ManualEntryTrigger({
  density,
  disabled,
  onOpen,
}: {
  density: Density;
  disabled: boolean;
  onOpen: () => void;
}) {
  if (density === "row") {
    return (
      <Button
        type="button"
        variant="quiet"
        size="compact"
        disabled={disabled}
        onClick={onOpen}
        className="px-1.5"
      >
        <Plus className="size-3.5" aria-hidden />
        Enter address manually
      </Button>
    );
  }
  return (
    <Button
      type="button"
      variant="link"
      size="bare"
      disabled={disabled}
      onClick={onOpen}
      className="self-start justify-self-start underline"
    >
      Enter address manually
    </Button>
  );
}
